import { useQuery } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { api } from "../../shared/api/client";
import type { EmployeeSummary, PageResponse } from "../../shared/types/api";
import { Textarea, cn } from "../../shared/ui";

/** How many people to offer at once. More than this and nobody is reading, they are scrolling. */
const SUGGESTIONS = 6;

/**
 * A comment box that understands `@`.
 *
 * <p>Typing `@` and a few letters offers the people it could be; choosing one writes their name
 * into the text and remembers who they are. The name is what the reader sees and the id is what
 * gets notified — kept apart on purpose, because a name typed into a sentence is just a sentence,
 * and the server has no way to tell which Priya was meant.
 *
 * <p>Whoever is named is looked up again when the comment is saved: a mention whose name has been
 * edited out of the text is dropped, so nobody is notified about a comment that no longer
 * mentions them.
 */
export function MentionBox({
  value,
  onChange,
  mentioned,
  onMentionedChange,
  placeholder,
  rows = 3,
}: {
  value: string;
  onChange: (value: string) => void;
  /** Who is currently named, as id → name. */
  mentioned: Record<string, string>;
  onMentionedChange: (mentioned: Record<string, string>) => void;
  placeholder?: string;
  rows?: number;
}) {
  const box = useRef<HTMLTextAreaElement>(null);
  const [query, setQuery] = useState<string | null>(null);
  const [highlighted, setHighlighted] = useState(0);

  const people = useQuery({
    queryKey: ["employees", "mentions"],
    queryFn: () =>
      api.get<PageResponse<EmployeeSummary>>(
        "/api/v1/employees?active=true&size=200&sort=fullName,asc",
      ),
    // Only once somebody has actually typed an @: a comment box should cost nothing to open.
    enabled: query != null,
    staleTime: 5 * 60_000,
  });

  const matches =
    query == null
      ? []
      : (people.data?.content ?? [])
          .filter((person) =>
            person.fullName.toLowerCase().includes(query.toLowerCase()),
          )
          .slice(0, SUGGESTIONS);

  /**
   * The `@word` being typed at the cursor, if any.
   *
   * <p>Only ever the run immediately before the caret, and only when it starts a word. An `@` in
   * the middle of an email address is not somebody reaching for a name.
   */
  const partialAt = (text: string, caret: number): string | null => {
    const upToCaret = text.slice(0, caret);
    const at = upToCaret.lastIndexOf("@");
    if (at < 0) {
      return null;
    }
    const before = at === 0 ? " " : upToCaret[at - 1]!;
    if (!/\s/.test(before)) {
      return null;
    }
    const typed = upToCaret.slice(at + 1);
    return /^[\p{L}\p{N} ]{0,30}$/u.test(typed) ? typed : null;
  };

  const handleChange = (text: string) => {
    onChange(text);
    const caret = box.current?.selectionStart ?? text.length;
    setQuery(partialAt(text, caret));
    setHighlighted(0);
  };

  const choose = (person: EmployeeSummary) => {
    const caret = box.current?.selectionStart ?? value.length;
    const upToCaret = value.slice(0, caret);
    const at = upToCaret.lastIndexOf("@");
    const next = `${value.slice(0, at)}@${person.fullName} ${value.slice(caret)}`;

    onChange(next);
    onMentionedChange({ ...mentioned, [person.id]: person.fullName });
    setQuery(null);

    // Put the caret after the name just inserted rather than at the end of the comment.
    const resume = at + person.fullName.length + 2;
    requestAnimationFrame(() => {
      box.current?.focus();
      box.current?.setSelectionRange(resume, resume);
    });
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (query == null || matches.length === 0) {
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlighted((at) => (at + 1) % matches.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlighted((at) => (at - 1 + matches.length) % matches.length);
    } else if (event.key === "Enter" || event.key === "Tab") {
      event.preventDefault();
      choose(matches[highlighted]!);
    } else if (event.key === "Escape") {
      setQuery(null);
    }
  };

  return (
    <div className="relative">
      <Textarea
        ref={box}
        rows={rows}
        value={value}
        placeholder={placeholder}
        onChange={(event) => handleChange(event.target.value)}
        onKeyDown={onKeyDown}
        // Closed on the way out rather than on blur alone, so clicking a suggestion still lands.
        onBlur={() => window.setTimeout(() => setQuery(null), 150)}
      />

      {query != null && matches.length > 0 && (
        <ul
          role="listbox"
          aria-label="People you can mention"
          className="absolute bottom-full z-20 mb-1 w-72 overflow-hidden rounded-lg border border-ink-200 bg-surface shadow-lg"
        >
          {matches.map((person, index) => (
            <li key={person.id}>
              <button
                type="button"
                role="option"
                aria-selected={index === highlighted}
                /* Named explicitly: the designation beside it is there to tell two people with
                   the same name apart, and read aloud it runs into the name as one word. */
                aria-label={person.fullName}
                className={cn(
                  "w-full px-3 py-2 text-left text-sm transition-colors hover:bg-ink-50",
                  index === highlighted && "bg-brand-50",
                )}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => choose(person)}
              >
                <span className="font-medium text-ink-900">{person.fullName}</span>
                <span className="ml-2 text-xs text-ink-500">{person.designation}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Which of the people named are still named, given what the text now says.
 *
 * <p>Called at the moment of posting. Somebody who chose a name and then deleted it from the
 * sentence has un-mentioned them, and notifying them anyway would be telling them about a comment
 * that does not mention them.
 */
export function mentionsStillIn(
  text: string,
  mentioned: Record<string, string>,
): string[] {
  return Object.entries(mentioned)
    .filter(([, name]) => text.includes(`@${name}`))
    .map(([id]) => id);
}
